import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, NameCell } from '@/components/TableCells'
import type { CustomEventType } from '@/services/networks/customEventTypesApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  valueType: 'valueType',
  type: 'type',
  conversionType: 'conversionType',
  objectState: 'objectState',
  priority: 'priority',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface CustomEventTypeTableProps {
  customEvents: CustomEventType[]
  loading?: boolean
  emptyMessage?: string
  selection?: CustomEventType[]
  onSelectionChange?: (customEvents: CustomEventType[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function CustomEventTypeTable({
  customEvents,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: CustomEventTypeTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<CustomEventType, unknown>[] = [
    ...(selectable ? [selectionColumn<CustomEventType>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {row.original.description || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'valueType',
      header: t('customEvents.valueType'),
      size: 130,
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {row.original.valueType ? t(`customEvents.valueTypes.${row.original.valueType}`) : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'type',
      header: t('customEvents.displayType'),
      size: 110,
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {t(`customEvents.displayTypes.${row.original.type}`)}
        </span>
      ),
    },
    {
      accessorKey: 'conversionType',
      header: t('customEvents.conversionType'),
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {row.original.conversionType
            ? t(`customEvents.conversionTypes.${row.original.conversionType}`)
            : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<CustomEventType>({
      accessorKey: 'priority',
      header: t('customEvents.priority'),
      size: 90,
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">{row.original.priority || '—'}</span>
      ),
    }),
    optionalColumn<CustomEventType>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<CustomEventType>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={customEvents}
      loading={loading}
      emptyMessage={emptyMessage ?? t('customEvents.noCustomEventsFound')}
      columnsKey="customEvents"
      onRowClick={(row) => navigate(`/admin/custom-events/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
